import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, Shield, Zap, Users,
  CheckCircle2, ArrowLeft, Globe, TrendingUp, CreditCard, MessageCircle,
  ChevronDown, ShieldCheck, Lock
} from "lucide-react";
import Seo from "@/components/Seo";
const FeatureShowcase = React.lazy(() => import("@/components/landing/FeatureShowcase"));
const DesignShowcase = React.lazy(() => import("@/components/landing/DesignShowcase"));
const WhatsAppDemo = React.lazy(() => import("@/components/landing/WhatsAppDemo"));
import CountUp from "@/components/landing/CountUp";
import RotatingText from "@/components/landing/RotatingText";
import { useNoMotion } from "@/lib/a11yPrefs";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};

const floatingAnimation = {
  y: ["-10px", "10px"],
  transition: {
    y: {
      duration: 2,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut"
    }
  }
};

const upcomingFeatures = [
  { icon: CreditCard, title: "סליקת אשראי", desc: "קבלת תשלומים באשראי, אפל פיי וגוגל פיי ישירות מהחשבונית" },
  { icon: Users, title: "פורטל לקוחות", desc: "אזור אישי ללקוחות לצפייה בחשבוניות, היסטוריית מסמכים ותשלום אונליין" }
];

const stats = [
  { to: 30, suffix: " שניות", label: "להפקת מסמך" },
  { to: 100, suffix: "%", label: "תואם רשות המסים" },
  { to: 16, suffix: "", label: "תבניות וגופנים לבחירה" },
  { value: "חינם", label: "להתחלה, ללא כרטיס אשראי" },
];

const whyHeshbit = [
  { icon: Zap, title: "מהיר יותר", desc: "מסמך מקצועי מוכן ב-30 שניות — לא בדקות ולא בטפסים." },
  { icon: CheckCircle2, title: "פשוט יותר", desc: "ממשק עברי נקי, בלי עקומת למידה ובלי צורך ברואה חשבון." },
  { icon: MessageCircle, title: "חכם יותר", desc: "בוט WhatsApp ובינה מלאכותית שמפיקים חשבונית משיחה (בקרוב)." },
  { icon: CreditCard, title: "משתלם יותר", desc: "מתחילים בחינם — בלי כרטיס אשראי ובלי התחייבות." },
];

// הוכחה חברתית כנה — עובדות מאומתות, לא ביקורות מזויפות.
const trustPoints = [
  { icon: Shield, title: "תואם 100% רשות המסים", desc: "מספרי הקצאה רשמיים, חשבונית אלקטרונית מול שע\"מ, ודיווח מע\"מ עם קובץ PCN — כל מסמך עומד בדרישות החוק." },
  { icon: ShieldCheck, title: "חתום דיגיטלית וניתן לאימות", desc: "כל מסמך נחתם דיגיטלית עם קוד QR — הלקוח סורק ומאמת את מקוריותו ישירות באתר. שקיפות מלאה." },
  { icon: Lock, title: "מאובטח ומגובה בענן", desc: "המידע שלכם מוצפן, מגובה אוטומטית ונגיש מכל מכשיר — בלי לאבד מסמך ובלי התקנות." },
];

const useCases = [
  { icon: Zap, audience: "עצמאים ופרילנסרים", text: "מסמך מקצועי מוכן בדקה, חתום דיגיטלית, ונשלח ללקוח בוואטסאפ — בלי רואה חשבון." },
  { icon: FileText, audience: "רואי חשבון ומנהלי כספים", text: "דוחות מע\"מ ושנתיים מחושבים אוטומטית — בלי טעויות ובלי אקסל." },
  { icon: TrendingUp, audience: "עסקים קטנים וצומחים", text: "מתחילים בחינם, ומשדרגים בקלות כשהעסק גדל. הכל במקום אחד." },
];

const faqs = [
  { q: "האם המערכת מותאמת לדרישות רשות המסים?", a: "כן! המערכת מייצרת מספרי הקצאה, תומכת בחשבונית אלקטרונית ומפיקה דיווחי מע\"מ תואמים לשע\"מ." },
  { q: "כמה זמן לוקח ליצור חשבונית?", a: "פחות מ-30 שניות. בחרו לקוח, הוסיפו פריטים ושלחו — זה הכל." },
  { q: "האם יש תקופת ניסיון?", a: "המסלול החינמי כולל 20 מסמכים בחודש ללא הגבלת זמן. אין צורך בכרטיס אשראי." },
  { q: "האם הנתונים שלי מאובטחים?", a: "בהחלט. אנחנו משתמשים בהצפנה מתקדמת, גיבויים אוטומטיים ועומדים בתקני אבטחה מחמירים." },
  { q: "אפשר לייצא נתונים?", a: "כן, תומכים בייצוא ל-PDF, Excel, CSV ו-JSON לגיבוי מלא." },
  { q: "איך מוציאים חשבונית מס?", a: "בוחרים לקוח, מוסיפים פריטים ולוחצים שליחה — חשבית מפיקה חשבונית מס תקינה עם כל שדות החובה, תוך פחות מ-30 שניות." },
  { q: "מה זה מספר הקצאה ומתי צריך אותו?", a: "מספר הקצאה הוא אישור של רשות המסים לחשבונית מס מעל סף מסוים (מודל חשבוניות ישראל). חשבית מזהה מתי נדרש מספר הקצאה ומנחה אתכם בתהליך." },
  { q: "אפשר להוציא חשבונית כעוסק פטור?", a: "כן. עוסק פטור מפיק קבלה או חשבונית ללא מע\"מ, ועוסק מורשה מפיק חשבונית מס — חשבית תומכת בשני סוגי העוסקים." },
  { q: "אפשר לשלוח חשבונית בוואטסאפ?", a: "בהחלט. אפשר לשלוח כל מסמך ללקוח ישירות לוואטסאפ או במייל, כקובץ PDF מוכן וחתום דיגיטלית." },
];

// Live, looping product demo — a tax invoice that builds itself (customer →
// line items → totals tick up → "sent on WhatsApp ✓"). Conveys speed & ease.
function LiveInvoiceDemo() {
  const [step, setStep] = React.useState(0);
  const noMotion = useNoMotion();
  React.useEffect(() => {
    // בעצירת אנימציות מציגים את החשבונית המוגמרת במקום להריץ את ההדגמה
    if (noMotion) { setStep(5); return; }
    const id = setInterval(() => setStep((s) => (s + 1) % 6), 1300);
    return () => clearInterval(id);
  }, [noMotion]);

  const items = [
    { name: "ייעוץ עסקי", qty: 4, price: 450 },
    { name: "עיצוב מותג", qty: 1, price: 1200 },
    { name: "ניהול קמפיין", qty: 2, price: 800 },
  ];
  const visibleCount = Math.max(0, Math.min(step - 1, items.length)); // 2→1, 3→2, 4→3
  const subtotal = items.slice(0, visibleCount).reduce((s, it) => s + it.qty * it.price, 0);
  const vat = Math.round(subtotal * 0.18);
  const total = subtotal + vat;
  const showTotals = step >= 2;
  const sent = step >= 5;
  const fmt = (n) => "₪" + n.toLocaleString();

  return (
    <div className="p-5 md:p-8 bg-gradient-to-br from-background to-muted/40 relative" dir="rtl" style={{ minHeight: 372 }}>
      <span className="absolute top-3 left-3 z-10 text-[10px] font-bold bg-muted text-muted-foreground px-2 py-0.5 rounded-full border border-border">דוגמה</span>
      {/* header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center text-white font-bold">ע</div>
          <div className="text-right">
            <div className="font-bold text-foreground text-sm">העסק שלי בע"מ</div>
            <div className="text-xs text-muted-foreground">ע.מ 514123456</div>
          </div>
        </div>
        <div className="text-left">
          <div className="font-heading font-extrabold text-foreground">חשבונית מס</div>
          <div className="text-xs text-muted-foreground">#1042</div>
        </div>
      </div>

      {/* customer */}
      <motion.div animate={{ opacity: step >= 1 ? 1 : 0.12, x: step >= 1 ? 0 : 10 }} transition={{ duration: 0.4 }} className="mb-4 text-sm">
        <span className="text-muted-foreground">לכבוד: </span>
        <span className="font-bold text-foreground">דני ספרים בע"מ</span>
      </motion.div>

      {/* line items (always rendered → stable height; opacity animates) */}
      <div className="space-y-2 mb-4">
        {items.map((it, i) => {
          const visible = i < visibleCount;
          return (
            <motion.div
              key={i}
              animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 10 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="flex items-center justify-between bg-card border border-border rounded-xl px-4 py-2.5 text-sm"
            >
              <span className="font-medium text-foreground flex-1">{it.name}</span>
              <span className="text-muted-foreground mx-3">{it.qty} × {fmt(it.price)}</span>
              <span className="font-bold text-foreground">{fmt(it.qty * it.price)}</span>
            </motion.div>
          );
        })}
      </div>

      {/* totals — appear at step 2 and grow as items add (live calc) */}
      <motion.div animate={{ opacity: showTotals ? 1 : 0 }} transition={{ duration: 0.4 }} className="border-t border-border pt-3 space-y-1 text-sm">
        <div className="flex justify-between text-muted-foreground"><span>סה"כ לפני מע"מ</span><span className="tabular-nums">{fmt(subtotal)}</span></div>
        <div className="flex justify-between text-muted-foreground"><span>מע"מ 18%</span><span className="tabular-nums">{fmt(vat)}</span></div>
        <div className="flex justify-between font-extrabold text-foreground text-base"><span>סה"כ לתשלום</span><span className="gradient-text tabular-nums">{fmt(total)}</span></div>
      </motion.div>

      {/* sent confirmation */}
      <AnimatePresence>
        {sent && (
          <motion.div
            initial={{ scale: 0.6, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.6, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 18 }}
            className="absolute bottom-4 left-4 flex items-center gap-2 bg-green-500 text-white font-bold text-sm px-4 py-2 rounded-full shadow-lg shadow-green-500/30"
          >
            <CheckCircle2 className="w-4 h-4" /> נשלח בוואטסאפ ✓
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LazySection({ children, height = "200px" }) {
  const [isIntersecting, setIsIntersecting] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ minHeight: isIntersecting ? "auto" : height }}>
      {isIntersecting ? (
        <React.Suspense fallback={<div style={{ minHeight: height }} className="flex items-center justify-center text-muted-foreground font-bold">טוען...</div>}>
          {children}
        </React.Suspense>
      ) : null}
    </div>
  );
}

export default function Landing() {
  const [openFaq, setOpenFaq] = React.useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="overflow-hidden">
      <Seo path="/" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      {/* Hero — dual column: value text + live invoice mockup (product visible above the fold) */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
        {/* Animated Background blobs — CSS animation (compositor, off the main thread) */}
        <div className="hero-blob-a absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-primary/20 blur-[120px] -z-10" aria-hidden="true" />
        <div className="hero-blob-b absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-accent/20 blur-[100px] -z-10" aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            {/* Text column */}
            <motion.div
              initial={false}
              animate="visible"
              variants={stagger}
              className="text-center lg:text-right"
            >
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-indigo-100 text-indigo-900 text-sm font-bold mb-6 shadow-sm border border-indigo-200 backdrop-blur-sm dark:bg-indigo-900/50 dark:text-white dark:border-indigo-800">
                <Zap className="w-4 h-4 fill-primary" />
                <span>חשבית · מנהלת החשבוניות הדיגיטלית שלך</span>
              </motion.div>

              <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-foreground leading-tight mb-5 drop-shadow-sm">
                הפקת{" "}
                <RotatingText
                  words={["חשבוניות ירוקות", "חשבוניות דיגיטליות", "חשבוניות אונליין", "קבלות וחשבוניות"]}
                  className="gradient-text bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-500"
                />
                <br />
                בקליק אחד — לעסק הישראלי
              </motion.h1>

              <motion.p variants={fadeUp} className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8 font-medium">
                הפיקו <span className="text-foreground font-bold">חשבונית ירוקה (דיגיטלית)</span>, קבלה והצעת מחיר תוך שניות — גם מהוואטסאפ. תואם רשות המסים, כולל חתימה דיגיטלית, מספרי הקצאה ודיווח מע"מ אוטומטי.
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button asChild size="lg" className="rounded-full gradient-primary text-white text-lg px-9 py-6 shadow-2xl shadow-primary/40 hover:shadow-primary/60 hover:-translate-y-1 transition-all font-bold"><Link to="/register">
                    התחילו עכשיו בחינם
                    <ArrowLeft className="w-6 h-6 mr-2" />
                  </Link></Button>
                <Button asChild size="lg" variant="outline" className="rounded-full text-lg px-9 py-6 border-2 font-bold"><Link to="/features/">
                    גלו את התכונות
                  </Link></Button>
              </motion.div>

              <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-3 text-sm md:text-base font-medium text-muted-foreground">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-5 h-5 text-success" /> ללא כרטיס אשראי</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-5 h-5 text-success" /> 20 מסמכים חינם</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-5 h-5 text-success" /> תואם רשות המסים</span>
              </motion.div>
            </motion.div>

            {/* Live invoice mockup — instantly shows this is an invoicing app */}
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.3, type: "spring" }}
              className="rounded-2xl overflow-hidden shadow-2xl border border-border bg-card/80 backdrop-blur-md"
            >
              <div className="h-8 bg-muted border-b border-border flex items-center px-4 gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
              <LiveInvoiceDemo />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tax-compliance trust band — highlights official allocation numbers */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="rounded-3xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 p-8 md:p-12 text-center relative overflow-hidden"
          >
            <div className="absolute -right-16 -top-16 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary font-bold text-sm mb-5">
                <Shield className="w-4 h-4 fill-primary/30" /> תואם 100% לרשות המסים
              </div>
              <h2 className="text-3xl md:text-5xl font-heading font-extrabold text-foreground mb-4 leading-tight">
                כולל הנפקת <span className="gradient-text bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-500">מספרי הקצאה</span>
                <br className="hidden md:block" /> לפי חוק חשבוניות ישראל
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 font-medium leading-relaxed">
                חשבונית מס מעל ₪5,000? המערכת מנחה אתכם לקבל מספר הקצאה רשמי מרשות המסים בלחיצה — והוא מודפס אוטומטית על המסמך. בלי רואה חשבון, בלי טפסים מסובכים.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm md:text-base font-bold text-foreground">
                <span className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-success" /> מספרי הקצאה</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-success" /> חשבונית אלקטרונית</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-success" /> דיווח מע"מ אוטומטי</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-success" /> חתימה דיגיטלית</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WhatsApp — flagship differentiator */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="rounded-3xl border-2 border-green-500/20 bg-gradient-to-br from-green-500/5 to-emerald-500/5 p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center"
          >
            {/* Copy */}
            <div className="text-center md:text-right">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/10 text-green-600 font-bold text-sm mb-5">
                <MessageCircle className="w-4 h-4 fill-green-500/20" /> חדש · WhatsApp
              </div>
              <h2 className="text-3xl md:text-5xl font-heading font-extrabold text-foreground mb-4 leading-tight">
                חשבוניות ישר <span className="text-green-600">לוואטסאפ</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6 font-medium leading-relaxed">
                שלחו ללקוח חשבונית או קבלה ישירות לוואטסאפ — הוא מקבל אותן לנייד תוך שניות.
                <br />
                <span className="text-foreground font-bold">בקרוב — שלח הודעה, חשבית מוציאה חשבונית:</span> כתבו לבוט "תוציא חשבונית של ₪1,500 לאיציק מרגליות על עבודת אינסטלציה" — והיא תופק ותישלח אוטומטית.
              </p>
              <ul className="space-y-2.5 mb-8 inline-block text-right">
                {["שליחה בלחיצה אחת — בלי להעתיק קישורים", "הלקוח מקבל PDF מוכן ומקצועי", "בקרוב: הפקה חכמה משיחה בשפה חופשית"].map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-foreground font-medium">
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
              <div>
                {/* asChild — <a> שעוטף <button> הוא HTML לא תקין ויוצר שני יעדי מגע חופפים.
                    green-700 (ולא green-600) כי טקסט לבן ב-18px דורש 4.5:1 — כאן 5.0:1. */}
                <Button asChild size="lg" className="rounded-full bg-green-700 hover:bg-green-800 text-white text-lg px-8 py-6 shadow-xl shadow-green-700/30 font-bold">
                  <Link to="/register">
                    נסו בחינם
                    <ArrowLeft className="w-5 h-5 mr-2" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Chat mockup — animated demo */}
            <LazySection height="450px">
              <WhatsAppDemo />
            </LazySection>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, i) => (
              <motion.div key={i} variants={fadeUp} className="text-center p-6 rounded-2xl hover:bg-muted/50 transition-colors">
                <div className="text-4xl md:text-5xl font-heading font-extrabold gradient-text mb-2 drop-shadow-sm">
                  {stat.value ? stat.value : <CountUp to={stat.to} suffix={stat.suffix} />}
                </div>
                <div className="text-base text-muted-foreground font-bold">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Heshbit — comparative benefits (sales) */}
      <section className="py-24 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.div variants={fadeUp} className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-bold text-sm mb-4">למה חשבית?</motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-6xl font-heading font-extrabold text-foreground mb-4">החשבוניות שלך, רק טוב יותר</motion.h2>
            <motion.p variants={fadeUp} className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium">כל מה שצריך לניהול חשבוניות וקבלות — מהיר, פשוט וחכם.</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {whyHeshbit.map((w, i) => {
              const Icon = w.icon;
              return (
                <motion.div key={i} variants={fadeUp} whileHover={{ y: -6 }} className="bg-background border border-border rounded-3xl p-7 text-center shadow-sm hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 rounded-2xl gradient-primary mx-auto mb-5 flex items-center justify-center shadow-lg shadow-primary/30">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-heading font-extrabold text-foreground mb-2">{w.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">{w.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-20"
          >
            <motion.div variants={fadeUp} className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent font-bold text-sm mb-4">פיצ'רים מתקדמים</motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-6xl font-heading font-extrabold text-foreground mb-6">
              חשבוניות, קבלות והצעות מחיר — הכל במקום אחד
            </motion.h2>
            <motion.p variants={fadeUp} className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium">
              כלים מקצועיים שמפשטים את הנהלת החשבונות ומייעלים את העבודה היומית
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
          >
            <LazySection height="400px">
              <FeatureShowcase />
            </LazySection>
          </motion.div>
        </div>
      </section>

      {/* Customize — live design preview (mirrors competitor "בהתאמה אישית") */}
      <section className="py-24 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.div variants={fadeUp} className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent font-bold text-sm mb-4">בהתאמה אישית</motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-6xl font-heading font-extrabold text-foreground mb-6">המסמכים שלכם, המותג שלכם</motion.h2>
            <motion.p variants={fadeUp} className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium">בחרו מתוך תבניות מוכנות, גופנים וצבעים — וראו תצוגה מקדימה חיה. בדיוק כמו במערכת.</motion.p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5 }}>
            <LazySection height="500px">
              <DesignShowcase />
            </LazySection>
          </motion.div>
        </div>
      </section>

      {/* Coming Soon Features - Based on Competitor Research */}
      <section className="py-24 bg-muted/30 border-y border-border relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.div variants={fadeUp} className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-bold text-sm mb-4">מפת הדרכים שלנו</motion.div>
            <motion.h2 variants={fadeUp} className="text-2xl md:text-4xl font-heading font-extrabold text-foreground mb-4">
              כבר עכשיו מלא — ועוד בדרך
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-muted-foreground max-w-2xl mx-auto font-medium">
              חשבית כבר עושה את כל מה שצריך להפקת חשבוניות וניהול העסק — ואלה הפיצ'רים הבאים שאנחנו בונים:
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {upcomingFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={i} variants={fadeUp} className="bg-background border border-primary/20 rounded-3xl p-8 relative overflow-hidden group">
                  <div className="absolute -right-10 -top-10 w-40 h-40 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-colors"></div>
                  <Icon className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-2xl font-heading font-bold text-foreground mb-3">{f.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-lg">{f.desc}</p>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20">
            <motion.h2 variants={fadeUp} className="text-4xl md:text-6xl font-heading font-extrabold text-foreground mb-6">
              איך מוציאים חשבונית? פשוט מאוד
            </motion.h2>
            <motion.p variants={fadeUp} className="text-xl text-muted-foreground font-medium">שלושה צעדים ואתם בדרך</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-12 relative">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-12 right-[15%] left-[15%] h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent -z-10"></div>
            
            {[
              { step: "1", icon: Globe, title: "הירשמו בחינם", desc: "צרו חשבון תוך 30 שניות. הגדירו את פרטי העסק ואתם מוכנים." },
              { step: "2", icon: FileText, title: "צרו מסמכים", desc: "הפיקו חשבוניות, קבלות והצעות מחיר מקצועיות עם חתימה דיגיטלית." },
              { step: "3", icon: TrendingUp, title: "עקבו ודווחו", desc: "צפו בדוחות, דיווחי מע\"מ ותזרים — הכל אוטומטי." },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div key={i} variants={fadeUp} className="text-center">
                  <motion.div animate={floatingAnimation} className="relative mx-auto w-24 h-24 rounded-full gradient-primary flex items-center justify-center mb-8 shadow-xl shadow-primary/30 border-4 border-card">
                    <Icon className="w-10 h-10 text-white" />
                    <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-foreground text-background flex items-center justify-center text-lg font-bold shadow-lg">
                      {item.step}
                    </div>
                  </motion.div>
                  <h3 className="text-2xl font-heading font-bold text-foreground mb-4">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-lg">{item.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Use cases — honest, by audience (replaces fabricated testimonials) */}
      <section className="py-24 bg-muted/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.h2 variants={fadeUp} className="text-4xl md:text-6xl font-heading font-extrabold text-foreground mb-4">מתאים לכל עסק</motion.h2>
            <motion.p variants={fadeUp} className="text-xl text-muted-foreground font-medium">מפרילנסרים ועד חברות צומחות — חשבית מתאימה לדרך העבודה שלכם</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-8">
            {useCases.map((u, i) => {
              const Icon = u.icon;
              return (
                <motion.div key={i} variants={fadeUp} whileHover={{ y: -6 }} className="bg-card border border-border rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-foreground mb-3">{u.audience}</h3>
                  <p className="text-muted-foreground leading-relaxed text-lg">{u.text}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Credibility — honest social proof (real compliance facts, not fabricated reviews).
          A real customer-testimonials block can slot in here once the owner provides genuine reviews. */}
      <section className="py-24 bg-card border-y border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.div variants={fadeUp} className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-bold text-sm mb-4">בנוי לאמון</motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-6xl font-heading font-extrabold text-foreground mb-4">למה אפשר לסמוך על חשבית</motion.h2>
            <motion.p variants={fadeUp} className="text-xl text-muted-foreground font-medium max-w-2xl mx-auto">לא הבטחות — תאימות מלאה לרשות המסים, חתימה דיגיטלית ואבטחה בכל מסמך שאתם מפיקים.</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-8 mb-12">
            {trustPoints.map((t, i) => {
              const Icon = t.icon;
              return (
                <motion.div key={i} variants={fadeUp} whileHover={{ y: -6 }} className="bg-background border border-border rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5 mx-auto">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-foreground mb-3">{t.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{t.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>

          {/* תגי תאימות אמיתיים — כל אחד הוא יכולת קיימת במערכת */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="flex flex-wrap items-center justify-center gap-3">
            {["תואם רשות המסים", "מספרי הקצאה (שע\"מ)", "חשבונית אלקטרונית", "קובץ PCN למע\"מ", "טופס 856", "דוח שנתי 1301", "חתימה דיגיטלית + אימות QR", "גיבוי בענן"].map((b, i) => (
              <span key={i} className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-bold text-foreground">
                <CheckCircle2 className="w-4 h-4 text-success shrink-0" /> {b}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-card">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-heading font-extrabold text-foreground mb-4">שאלות נפוצות</motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-muted-foreground font-medium">כל מה שרציתם לדעת לפני שמתחילים</motion.p>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="border border-border rounded-2xl overflow-hidden bg-background"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-right font-bold text-foreground hover:bg-muted/40 transition-colors"
                >
                  <span>{f.q}</span>
                  <ChevronDown className={`w-5 h-5 shrink-0 text-primary transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-muted-foreground leading-relaxed">{f.a}</div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-primary/5 -z-10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-6xl font-heading font-extrabold text-foreground mb-6">
              מוכנים לקחת את העסק קדימה?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-xl text-muted-foreground mb-12 font-medium max-w-2xl mx-auto">
              התחילו בחינם ונהלו את העסק בצורה חכמה, פשוטה ומהירה — בלי כרטיס אשראי
            </motion.p>
            <motion.div variants={fadeUp} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button asChild size="lg" className="rounded-full gradient-primary text-white text-2xl px-12 py-8 shadow-2xl shadow-primary/40 font-bold"><Link to="/register">
                  התחילו בחינם — ללא התחייבות
                  <ArrowLeft className="w-7 h-7 mr-3" />
                </Link></Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}